import { Component } from 'react'
import { Button } from '@/components/ui/button'
import { isChunkLoadError } from './chunkLoadError'

/**
 * Event dispatched on `window` when a render error is caught.
 *
 * The boundary deliberately does not know who (if anyone) is listening — this
 * mirrors the `limit-exceeded` CustomEvent already dispatched from
 * `src/api/client.js`. Error-reporting tooling subscribes to this; with no
 * listener the boundary still works, it just logs to the console.
 *
 * `detail`: `{ error, componentStack, boundary }`
 */
export const APP_ERROR_EVENT = 'app-error'

/**
 * Catches render errors below it and shows a recoverable fallback instead of a
 * blank page.
 *
 * React only invokes error boundaries for errors thrown during render,
 * lifecycle methods, and constructors — not for event handlers, async
 * callbacks, or rejected promises. Those are handled separately.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Subtree to protect
 * @param {string} [props.name='root'] - Identifies which boundary caught the
 *   error, so a report says whether the whole app or just a page failed
 * @param {string} [props.title] - Fallback heading
 * @param {boolean} [props.fullScreen=true] - Center in the viewport. Set false
 *   for a boundary nested inside existing chrome (header/sidebar).
 * @param {boolean} [props.floating=false] - A small card pinned to the
 *   corner, for a boundary around a piece of the shell (the assistant panel,
 *   the search dialog) that has no room of its own for a fallback.
 *
 * @example
 * // Page-level: keying on the pathname makes React remount the boundary on
 * // navigation, so a broken page recovers by itself when the user moves away.
 * <AppErrorBoundary name="page" fullScreen={false} key={location.pathname}>
 *   <Outlet />
 * </AppErrorBoundary>
 */
export default class AppErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { error: null }
    this.handleRetry = this.handleRetry.bind(this)
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  componentDidCatch(error, errorInfo) {
    const boundary = this.props.name || 'root'

    // Keep logging unconditionally: the console is the only record when no
    // reporting tool is listening.
    console.error(`[AppErrorBoundary:${boundary}]`, error, errorInfo?.componentStack)

    // Never let reporting turn a render error into a second error.
    try {
      window.dispatchEvent(
        new CustomEvent(APP_ERROR_EVENT, {
          detail: {
            error,
            componentStack: errorInfo?.componentStack ?? null,
            boundary,
          },
        })
      )
    } catch {
      // Ignore — the fallback below still renders.
    }
  }

  /** Clear the error and attempt to render children again. */
  handleRetry() {
    this.setState({ error: null })
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const {
      title = 'Something went wrong',
      fullScreen = true,
      floating = false,
    } = this.props
    const needsReload = isChunkLoadError(error)
    const reload = () => window.location.reload()

    if (floating) {
      return (
        <div role="alert" className="fixed right-4 bottom-4 z-50 max-w-sm space-y-3 rounded-control border border-line bg-surface p-4 text-left shadow-overlay">
          <p className="text-sm font-medium text-ink">{needsReload ? 'Graphit was updated' : title}</p>
          <p className="text-sm text-ink-2">
            {needsReload
              ? 'Reload the page to load the new version.'
              : 'This part of the page failed to load.'}
          </p>
          <Button onClick={needsReload ? reload : this.handleRetry}>
            {needsReload ? 'Reload' : 'Try again'}
          </Button>
        </div>
      )
    }

    return (
      <div
        className={
          fullScreen
            ? 'flex min-h-screen items-center justify-center bg-surface-2'
            : 'flex min-h-[60vh] items-center justify-center'
        }
      >
        <div className="text-center space-y-4 max-w-md px-6" role="alert">
          <h1 className="text-2xl font-bold text-ink">{needsReload ? 'Graphit was updated' : title}</h1>
          <p className="text-sm text-ink-2">
            {needsReload
              ? 'This page needs a reload to load the new version.'
              : 'This part of the page failed to load. Trying again often works; if it keeps happening, please report it so we can fix it.'}
          </p>
          {!needsReload && error.message && (
            <p className="text-xs text-ink-2 font-mono break-words">
              {error.message}
            </p>
          )}
          <div className="flex items-center justify-center gap-3 pt-2">
            {needsReload
              ? <Button onClick={reload}>Reload</Button>
              : <Button onClick={this.handleRetry}>Try again</Button>}
            {/* A plain anchor, not <Link>: the boundary must render even when
                the failure is in or above the router. */}
            <a
              href="/"
              className="inline-block px-6 py-2 border rounded-md hover:bg-surface-2 transition-colors text-sm text-ink"
            >
              Go home
            </a>
          </div>
        </div>
      </div>
    )
  }
}
